<script setup lang="ts">
import type { ProductListItem } from '~/types/product'
import ProductCard from '~/components/Product/ProductCard.vue'
import UiButton from '~/components/Ui/UiButton.vue'
import { Logger } from '~/composables/logger'

const props = defineProps<{
  searchString: string
}>()

const searchString = ref(props.searchString)
const products = ref<ProductListItem[]>([])

const pagination = usePagination({
  total: 1,
  pageSize: 25,
  currentPage: 1,
})
const requestParams = computed(() => ({
  searchString: searchString.value,
  pageNumber: pagination.currentPage.value,
}))
const isLastPage = computed<boolean>(() => {
  if (paginatedProductList.value) {
    return paginatedProductList.value.currentPage >= paginatedProductList.value.totalPages
  }

  return true
})

const {
  data: paginatedProductList,
  pending,
  execute,
  error,
} = await useFetch<ProductListItemPaginatedResult>('/api/products', {
  params: requestParams,
})

if (error.value) {
  pagination.total = 0
  Logger.error(error.value, 'Error in SearchResultProducts')
}

watch(
  () => props.searchString,
  (newValue) => {
    searchString.value = newValue
  },
)

watch(searchString, () => {
  products.value = []
})

watch(
  paginatedProductList,
  () => {
    if (paginatedProductList.value) {
      products.value.push(...paginatedProductList.value.data)
      pagination.total.value = paginatedProductList.value.totalRecords || 0
      pagination.pageSize.value = paginatedProductList.value.pageSize || 25
      pagination.currentPage.value = paginatedProductList.value.currentPage || 1
    }
  },
  { immediate: true },
)

const nextPage = () => {
  pagination.next()
  execute()
}
</script>

<template>
  <section>
    <h2 class="mb-6">
      Du sökte på
      <span class="font-bold text-black">{{ searchString }}</span>
      och fick
      <span class="font-bold text-black">{{ pagination.total }} träffar.</span>
    </h2>
    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
      <ProductCard v-for="product in products" :key="product.id" :product="product" :search-query="searchString" />
    </div>
    <div class="text-center">
      <UiButton v-if="!isLastPage" class="mt-5 px-16" :disabled="pending" @click="nextPage">Visa fler</UiButton>
    </div>
  </section>
</template>
